/**
 * TNCY-6 §a / FB-03 — Party picker (Customer / Contractor / Reporting-subcontractor).
 *
 * Customer + Contractor slots are EITHER:
 *   - "Share with a partner company" — pick a known tenant → creates the
 *     matching role grant (XT-D23 role mapping). This is the explicit-grant
 *     approach (Christian 2026-06-23): projects/v1 has no endpoint that
 *     writes `projects.*_tenant_id`, so the picker calls
 *     `POST …/access-grants` directly to achieve "share this project with
 *     <tenant> as <role>". The descriptive party-field write + XT-D23
 *     auto-grant is a logged follow-up.
 *   - "Address only (no sharing)" — a contact. A contact never shares
 *     (XT-D23); v1 has no contact directory wired, so this option is shown
 *     as the explicit NON-sharing choice and is disabled with a hint (the
 *     contact CRUD surface is out of scope for TNCY-6).
 *
 * The Reporting-subcontractor slot (FB-03 / PM-D30) is TENANT-ONLY — the
 * report is produced in OUR system, so a reporting party can only be a known
 * tenant, never a contact. It is a clear two-option choice instead:
 *   - "Our team (we do the reporting)" — the current tenant reports → no grant.
 *     If a reporting grant already exists, an offer to revoke it is shown.
 *   - "A partner company" — a partner tenant reports → required tenant picker →
 *     creates the `reporting_subcontractor` grant. The "address only / no
 *     sharing" (contact) option is NOT offered here.
 *
 * Gating: the tenant chooser is limited to the caller's known-tenants set
 * (active links only — XT-D26). There is no free-text tenant entry, so a
 * grant can never point at a stranger. When the set is empty, the picker
 * shows an "invite them first" prompt deep-linking to Partner Companies
 * (TNCY-5, /admin/partner-companies).
 *
 * Owner tenant_admin only — the parent panel gates visibility; the BE is
 * the security boundary (403 otherwise).
 */
import { useMemo, useState, type ReactElement } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type {
  AccessGrant,
  AccessRole,
  KnownTenant,
} from "../../tenancy/types.js";
import { ACCESS_ROLE_SURFACE, accessRoleLabel } from "./roleLabels.js";

export type PartyKind = "customer" | "contractor" | "reporting";

/** XT-D23 — project-level party field → access role mapping. */
const PARTY_ROLE: Record<PartyKind, AccessRole> = {
  customer: "project_customer",
  contractor: "project_contractor",
  reporting: "reporting_subcontractor",
};

const PARTY_TITLE: Record<PartyKind, string> = {
  customer: "Customer",
  contractor: "Contractor",
  reporting: "Report subcontractor",
};

export interface PartyPickerProps {
  kind: PartyKind;
  /** Active known tenants (XT-D26) — the only grantable set. */
  knownTenants: readonly KnownTenant[];
  knownTenantsLoading: boolean;
  /** Tenant ids that already hold this party's role on the project — shown as
   *  "already shared" so the admin doesn't double-grant. */
  alreadyGrantedTenantIds: ReadonlySet<string>;
  /** Fires the explicit grant (POST …/access-grants). */
  onShareWithTenant: (args: { tenantId: string; role: AccessRole }) => void;
  /** FB-03 (reporting only) — the existing active reporting grant, if any.
   *  When "Our team" is chosen and one exists, an offer to revoke it is shown. */
  existingGrant?: AccessGrant | null;
  /** FB-03 (reporting only) — revoke the existing reporting grant. */
  onRevoke?: (grant: AccessGrant) => void;
  pending?: boolean;
}

export function PartyPicker({
  kind,
  knownTenants,
  knownTenantsLoading,
  alreadyGrantedTenantIds,
  onShareWithTenant,
  existingGrant,
  onRevoke,
  pending,
}: PartyPickerProps): ReactElement {
  const role = PARTY_ROLE[kind];
  const isReporting = kind === "reporting";
  // Reporting (FB-03) is tenant-only with a two-option model: "Our team"
  // (no grant) vs "A partner company" (required tenant picker). Customer +
  // contractor keep the tenant / address-only (contact) modes.
  const [mode, setMode] = useState<"tenant" | "contact" | "our_team">(
    isReporting ? "our_team" : "tenant",
  );
  const [selected, setSelected] = useState<KnownTenant | null>(null);

  const options = useMemo(
    () =>
      [...knownTenants].sort((a, b) =>
        a.tenantName.localeCompare(b.tenantName),
      ),
    [knownTenants],
  );

  const noKnownTenants = !knownTenantsLoading && options.length === 0;
  const alreadyShared =
    selected != null && alreadyGrantedTenantIds.has(selected.tenantId);

  function handleShare(): void {
    if (!selected || alreadyShared) return;
    onShareWithTenant({ tenantId: selected.tenantId, role });
    setSelected(null);
  }

  return (
    <Box
      component="section"
      aria-label={`${PARTY_TITLE[kind]} party`}
      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
    >
      <Typography variant="subtitle2">{PARTY_TITLE[kind]}</Typography>
      <Typography variant="caption" color="text.secondary">
        {isReporting ? "Who will do the reporting?" : ACCESS_ROLE_SURFACE[role]}
      </Typography>

      {isReporting ? (
        <RadioGroup
          value={mode}
          onChange={(_e, v) => {
            setMode(v as "tenant" | "our_team");
            setSelected(null);
          }}
          aria-label="Who will do the reporting?"
        >
          <FormControlLabel
            value="our_team"
            control={<Radio size="small" />}
            label="Our team (we do the reporting)"
          />
          <FormControlLabel
            value="tenant"
            control={<Radio size="small" />}
            label="A partner company"
          />
        </RadioGroup>
      ) : (
        <RadioGroup
          row
          value={mode}
          onChange={(_e, v) => {
            setMode(v as "tenant" | "contact");
          }}
          aria-label={`${PARTY_TITLE[kind]} sharing mode`}
        >
          <FormControlLabel
            value="tenant"
            control={<Radio size="small" />}
            label="Share with a partner company"
          />
          <FormControlLabel
            value="contact"
            control={<Radio size="small" />}
            label="Address only (no sharing)"
          />
        </RadioGroup>
      )}

      {isReporting && mode === "our_team" ? (
        existingGrant ? (
          <Alert
            severity="info"
            variant="outlined"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  onRevoke?.(existingGrant);
                }}
                disabled={pending === true}
                aria-label={`Revoke ${existingGrant.tenantName}'s reporting access`}
              >
                Revoke
              </Button>
            }
          >
            {existingGrant.tenantName} currently does the reporting. Switch to
            our team to revoke their reporting access.
          </Alert>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Your team produces the report — no reporting subcontractor is set.
          </Typography>
        )
      ) : mode === "contact" ? (
        <Alert severity="info" variant="outlined">
          A contact is an address for reports / invoices only — it does not
          share the project (XT-D23). Contact management is a separate surface
          (coming soon).
        </Alert>
      ) : noKnownTenants ? (
        <Alert severity="warning" variant="outlined">
          You aren&apos;t linked to any partner companies yet. Invite them
          first, then you can share this project.{" "}
          <Link
            component={RouterLink}
            to="/admin/partner-companies"
            underline="always"
          >
            Go to Partner Companies
          </Link>
        </Alert>
      ) : (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Autocomplete<KnownTenant>
            sx={{ flex: 1, minWidth: 240 }}
            size="small"
            options={options}
            loading={knownTenantsLoading}
            value={selected}
            onChange={(_e, v) => {
              setSelected(v);
            }}
            getOptionLabel={(o) => o.tenantName}
            isOptionEqualToValue={(a, b) => a.tenantId === b.tenantId}
            renderInput={(params) => {
              // MUI renderInput params carry `size?: … | undefined`, which
              // trips `exactOptionalPropertyTypes`. Repo-wide pattern: cast
              // the pass-through (see ComponentModelAutocomplete).
              /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
              const passThrough: any = params;
              return (
                <TextField
                  {...passThrough}
                  label="Partner company"
                  placeholder="Search linked companies…"
                  aria-label={`${PARTY_TITLE[kind]} — partner company`}
                />
              );
            }}
          />
          <Button
            variant="contained"
            onClick={handleShare}
            disabled={selected === null || alreadyShared || pending === true}
            aria-label={`Share as ${accessRoleLabel(role)}`}
          >
            Share
          </Button>
        </Stack>
      )}

      {alreadyShared ? (
        <Typography variant="caption" color="text.secondary">
          {selected.tenantName} already has the {accessRoleLabel(role)} role on
          this project.
        </Typography>
      ) : null}
    </Box>
  );
}
